import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 ring-offset-background [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        /**
         * The only destructive treatment for icon-only or ghost actions (row
         * Delete, a picker's Remove). Solid `destructive` stays for labelled
         * confirm buttons — DESIGN_SYSTEM.md §3.
         */
        'ghost-destructive':
          'text-destructive-emphasis hover:bg-destructive-subtle hover:text-destructive-emphasis',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3',
        lg: 'h-10 rounded-md px-8',
        /** 36px — beside default-height controls in toolbars and headers. */
        icon: 'h-9 w-9',
        /** 32px — icon-only row actions. Use RowAction rather than this directly. */
        'icon-sm': 'h-8 w-8',
        /** 28px — controls embedded in another surface: field adornments, code wells, editor toolbars, map controls. */
        'icon-xs': 'h-7 w-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * An async action this button started is running (DESIGN_SYSTEM.md §5
   * loading vocabulary): the button disables, is aria-busy, and a spinner
   * replaces its leading icon — or its glyph, on an icon-only button. The label
   * never changes. Ignored with `asChild`, which has no children of its own to
   * manage.
   */
  loading?: boolean;
  /**
   * Rung 3 of the unavailable-actions ladder (DESIGN_SYSTEM.md §5
   * Permissions): the action is normally offered on this kind of record but
   * is blocked for this one, and nothing else on screen says why. The button
   * stays focusable with `aria-disabled` (never native `disabled`), ignores
   * clicks and keyboard activation — including a form's implicit submit — and
   * carries this sentence as its accessible description. Icon sizes show it in
   * a Tooltip; a labelled button puts the same sentence beside itself with
   * `DisabledReasonText`, because tooltips don't reach touch.
   *
   * Product language only, never a permission key. `loading` wins: native
   * `disabled` stays for an in-flight action. Ignored with `asChild`.
   */
  disabledReason?: string;
}

let warnedAsChildLoading = false;
let warnedAsChildReason = false;

const ICON_SIZES = new Set(['icon', 'icon-sm', 'icon-xs']);

/** Swallows activation on an aria-disabled button without stopping focus. */
const blockActivation = (event: React.MouseEvent<HTMLButtonElement>) => {
  // preventDefault also cancels the implicit submit that Enter in a form
  // field dispatches as a click on the default submit button.
  event.preventDefault();
  event.stopPropagation();
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabledReason, children, ...props }, ref) => {
    const reasonId = React.useId();
    if (asChild) {
      if (loading && import.meta.env.DEV && !warnedAsChildLoading) {
        warnedAsChildLoading = true;
        console.warn('Button: `loading` is not supported with `asChild` and was ignored.');
      }
      if (disabledReason && import.meta.env.DEV && !warnedAsChildReason) {
        warnedAsChildReason = true;
        console.warn('Button: `disabledReason` is not supported with `asChild` and was ignored.');
      }
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }
    const blocked = Boolean(disabledReason) && !loading;
    if (blocked) {
      const describedBy = [props['aria-describedby'], reasonId].filter(Boolean).join(' ');
      const button = (
        <button
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
          disabled={undefined}
          aria-disabled="true"
          aria-describedby={describedBy}
          onClick={blockActivation}
        >
          {children}
        </button>
      );
      // The sentence sits beside the button, not inside it, where it would
      // join the accessible name.
      const reason = (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      );
      if (size && ICON_SIZES.has(size)) {
        return (
          <>
            <Tooltip>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent className="max-w-xs">{disabledReason}</TooltipContent>
            </Tooltip>
            {reason}
          </>
        );
      }
      return (
        <>
          {button}
          {reason}
        </>
      );
    }
    return (
      <button
        className={cn(
          buttonVariants({ variant, size, className }),
          // Hides the caller's own icons (direct svg children) while the
          // spinner stands in for them; text children stay.
          loading && '[&>svg:not([data-spinner])]:hidden',
        )}
        ref={ref}
        {...props}
        disabled={props.disabled || loading}
        aria-busy={loading ? true : props['aria-busy']}
      >
        {loading ? <Loader2 aria-hidden="true" data-spinner="" className="animate-spin" /> : null}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';

interface DisabledReasonTextProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * The visible, touch-reachable copy of a labelled Button's `disabledReason`,
 * placed beside or under it where there is room (dialog sections, footers).
 * aria-hidden: the button's own description already carries the sentence, so
 * a screen reader hears it once.
 */
const DisabledReasonText: React.FC<DisabledReasonTextProps> = ({ children, className }) => (
  <p aria-hidden="true" data-disabled-reason="" className={cn('text-sm text-muted-foreground', className)}>
    {children}
  </p>
);

export { Button, buttonVariants, DisabledReasonText };
